import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { generatedFileResponseSchema } from "./imports.js";
import { stockQtySchema, stockVariantSchema } from "./inventory.js";
import { paisa } from "./money.js";
/** Transfers between locations (docs/specs/inventory.md "Transfers (B-26)", I-06). */
export const TRANSFER_STATUSES = [
    "draft",
    "requested",
    "approved",
    "dispatched",
    "received",
    "closed",
    "cancelled",
];
export const transferStatusSchema = z.enum(TRANSFER_STATUSES);
export const VARIANCE_REASONS = [
    "short_shipped",
    "damaged_in_transit",
    "lost_in_transit",
    "extra_received",
    "other",
];
export const varianceReasonSchema = z.enum(VARIANCE_REASONS);
export const MAX_TRANSFER_LINES = 500;
const positiveQty = stockQtySchema.refine((value) => BigInt(value) > 0n, "Must be more than zero");
const zeroOrMore = stockQtySchema.refine((value) => BigInt(value) >= 0n, "Cannot be negative");
const note = z.string().trim().max(500);
const version = z.int().min(1);
const step = { at: z.iso.datetime().nullable(), by: z.uuid().nullable() };
const stepSchema = z.object(step);
export const transferSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    fromLocationId: z.uuid(),
    toLocationId: z.uuid(),
    status: transferStatusSchema,
    note: z.string(),
    lineCount: z.int(),
    requested: stepSchema,
    approved: stepSchema,
    dispatched: stepSchema,
    received: stepSchema,
    closed: stepSchema,
    cancelled: stepSchema,
    version: z.int(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const transferLineSchema = z.object({
    id: z.uuid(),
    variantId: z.uuid(),
    variant: stockVariantSchema.extend({ barcodes: z.array(z.string()) }),
    requestedQty: stockQtySchema,
    sentQty: stockQtySchema.nullable(),
    receivedQty: stockQtySchema.nullable(),
    /** The source's average cost at dispatch, carried to the destination. */
    unitCostPaisa: paisa().nullable(),
    varianceReason: varianceReasonSchema.nullable(),
    varianceNote: z.string(),
});
export const transferDetailSchema = transferSchema.extend({ lines: z.array(transferLineSchema) });
export const transferListQuerySchema = z.object({
    status: transferStatusSchema.optional(),
    /** Transfers from or to this location (or, with `direction`, only out of / into it). */
    locationId: z.uuid().optional(),
    direction: z.enum(["in", "out"]).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const transferListSchema = z.object({
    data: z.array(transferSchema),
    nextCursor: z.uuid().nullable(),
});
const distinctVariants = (lines) => new Set(lines.map((line) => line.variantId.toLowerCase())).size === lines.length;
const lineInput = z.object({ variantId: z.uuid(), qty: positiveQty }).strict();
const lines = z.array(lineInput).min(1).max(MAX_TRANSFER_LINES).refine(distinctVariants, "Each variant once");
/** `POST /v1/transfers`: a draft. */
export const createTransferRequestSchema = z
    .object({ fromLocationId: z.uuid(), toLocationId: z.uuid(), note: note.default(""), lines })
    .strict()
    .refine((t) => t.fromLocationId.toLowerCase() !== t.toLocationId.toLowerCase(), {
    message: "From and to must differ",
    path: ["toLocationId"],
});
/** `PATCH /v1/transfers/{id}`: a draft's note and/or lines (replacing them all). */
export const updateTransferRequestSchema = z
    .object({ note: note.optional(), lines: lines.optional(), version })
    .strict();
/** request / approve / close / cancel: the version last read. */
export const transferActionRequestSchema = z.object({ version }).strict();
/** `POST …/dispatch`: what actually leaves; a line left out is sent as requested. */
export const dispatchTransferRequestSchema = z
    .object({
    version,
    lines: z
        .array(z.object({ lineId: z.uuid(), sentQty: zeroOrMore }).strict())
        .max(MAX_TRANSFER_LINES)
        .default([]),
})
    .strict();
/** `POST …/receive`: what arrived; a line left out arrived as sent. A difference needs a reason. */
export const receiveTransferRequestSchema = z
    .object({
    version,
    lines: z
        .array(z
        .object({
        lineId: z.uuid(),
        receivedQty: zeroOrMore,
        varianceReason: varianceReasonSchema.optional(),
        varianceNote: note.optional(),
    })
        .strict())
        .max(MAX_TRANSFER_LINES)
        .default([]),
})
    .strict();
/** Event data: the transfer and its quantities (no display names), so consumers never read back. */
export const transferEventSchema = transferSchema.extend({
    lines: z.array(transferLineSchema.omit({ variant: true })),
});
// Endpoints -------------------------------------------------------------------------------------------------------
const transfers = { tag: "inventory" };
const manage = { permission: "transfers.manage" };
const approveAccess = { permission: "transfers.approve" };
const view = { permission: "inventory.view" };
export const listTransfers = defineEndpoint({
    ...transfers,
    name: "listTransfers",
    method: "GET",
    path: "/v1/transfers",
    access: view,
    summary: "Transfers from or to the caller's locations, newest first",
    query: transferListQuerySchema,
    status: 200,
    response: transferListSchema,
});
export const createTransfer = defineEndpoint({
    ...transfers,
    name: "createTransfer",
    method: "POST",
    path: "/v1/transfers",
    access: manage,
    summary: "Start a transfer (draft)",
    body: createTransferRequestSchema,
    status: 201,
    response: transferDetailSchema,
    errors: ["STOCK_NOT_TRACKED", "LOCATION_INACTIVE"],
});
export const getTransfer = defineEndpoint({
    ...transfers,
    name: "getTransfer",
    method: "GET",
    path: "/v1/transfers/{id}",
    access: view,
    summary: "One transfer with its lines",
    params: idParamsSchema,
    status: 200,
    response: transferDetailSchema,
});
export const updateTransfer = defineEndpoint({
    ...transfers,
    name: "updateTransfer",
    method: "PATCH",
    path: "/v1/transfers/{id}",
    access: manage,
    summary: "Change a draft's note or lines",
    params: idParamsSchema,
    body: updateTransferRequestSchema,
    status: 200,
    response: transferDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION", "STOCK_NOT_TRACKED"],
});
const action = (name, verb, access, summary) => defineEndpoint({
    ...transfers,
    name,
    method: "POST",
    path: `/v1/transfers/{id}/${verb}`,
    access,
    summary,
    params: idParamsSchema,
    body: transferActionRequestSchema,
    status: 200,
    response: transferDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION", "LOCATION_INACTIVE"],
});
export const requestTransfer = action("requestTransfer", "request", manage, "Ask for approval");
export const approveTransfer = action("approveTransfer", "approve", approveAccess, "Approve (at the source)");
export const closeTransfer = action("closeTransfer", "close", approveAccess, "Accept a received transfer's variance");
// Approved transfers also need transfers.approve (checked by the service).
export const cancelTransfer = action("cancelTransfer", "cancel", manage, "Cancel before dispatch");
export const dispatchTransfer = defineEndpoint({
    ...transfers,
    name: "dispatchTransfer",
    method: "POST",
    path: "/v1/transfers/{id}/dispatch",
    access: manage,
    summary: "Send the goods: transfer_out at the source; in transit until received",
    params: idParamsSchema,
    body: dispatchTransferRequestSchema,
    status: 200,
    response: transferDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION", "INSUFFICIENT_STOCK", "LOCATION_INACTIVE"],
    idempotent: true,
});
export const receiveTransfer = defineEndpoint({
    ...transfers,
    name: "receiveTransfer",
    method: "POST",
    path: "/v1/transfers/{id}/receive",
    access: manage,
    summary: "Book what arrived: transfer_in at the destination, variances with reasons",
    params: idParamsSchema,
    body: receiveTransferRequestSchema,
    status: 200,
    response: transferDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION"],
    idempotent: true,
});
export const transferPdf = defineEndpoint({
    ...transfers,
    name: "transferPdf",
    method: "POST",
    path: "/v1/transfers/{id}/pdf",
    access: view,
    summary: "The transfer sheet as a PDF (signed URL)",
    params: idParamsSchema,
    status: 201,
    response: generatedFileResponseSchema,
});
//# sourceMappingURL=transfers.js.map